import {IconBolt, IconChevronRight, IconShoppingBag, IconTarget, IconTrophy, IconX} from "@tabler/icons-react";
import {useEffect, useMemo, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {useNavigate} from "react-router-dom";
import GamanzaWidgetSlot from "@/components/GamanzaWidgetSlot";
import useGamanzaWidgetHydration from "@/hooks/useGamanzaWidgetHydration";
import useGetUserInfo from "@/hooks/useGetUserInfo";
import {useAuthStore} from "@/store/useAuth.store";
import {useIsGamanzaUiEnabled} from "@/utils/gamanzaUi";
import styles from "./GamanzaPlayerDrawer.module.css";
import {GAMANZA_DRAWER_LINKS} from "./gamanzaDrawerLinks";
import {
  closeGamanzaPlayerDrawer,
  GAMANZA_PLAYER_DRAWER_CLOSE_EVENT,
  GAMANZA_PLAYER_DRAWER_OPEN_EVENT,
} from "./gamanzaPlayerDrawerEvents";

const LINK_ICONS = {
  "reward-shop": IconShoppingBag,
  "my-boosters": IconBolt,
  missions: IconTarget,
  tournaments: IconTrophy,
};

/**
 * Full-height right drawer opened from the Gamanza user avatar.
 * Hosts large avatar + username + points + nav links to widget pages.
 * Portaled to body so it stacks above the layout header (children sit in z-index: 1).
 */
const GamanzaPlayerDrawer = () => {
  const navigate = useNavigate();
  const widgetsRef = useRef(null);
  const [open, setOpen] = useState(false);
  const gamanzaUiEnabled = useIsGamanzaUiEnabled();
  const authStatus = useAuthStore(state => state.authStatus);
  const token = useAuthStore(state => state.token);
  const {data: userData} = useGetUserInfo();

  const displayName = useMemo(
    () =>
      userData?.given_name ||
      userData?.firstName ||
      userData?.preferred_username ||
      userData?.email?.split("@")[0] ||
      "",
    [userData]
  );

  const widgetsEnabled = open && gamanzaUiEnabled && authStatus === "authenticated" && Boolean(token);
  const {isHydrating} = useGamanzaWidgetHydration({
    enabled: widgetsEnabled,
    token,
    rootRef: widgetsRef,
  });

  useEffect(() => {
    const onOpen = () => setOpen(true);
    const onClose = () => setOpen(false);
    window.addEventListener(GAMANZA_PLAYER_DRAWER_OPEN_EVENT, onOpen);
    window.addEventListener(GAMANZA_PLAYER_DRAWER_CLOSE_EVENT, onClose);
    return () => {
      window.removeEventListener(GAMANZA_PLAYER_DRAWER_OPEN_EVENT, onOpen);
      window.removeEventListener(GAMANZA_PLAYER_DRAWER_CLOSE_EVENT, onClose);
    };
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = e => {
      if (e.key === "Escape") closeGamanzaPlayerDrawer();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open]);

  const handleClose = () => closeGamanzaPlayerDrawer();

  const handleNavigate = path => {
    handleClose();
    navigate(path);
  };

  if (typeof document === "undefined") return null;
  if (!open) return null;

  return createPortal(
    <>
      <div
        className={`${styles.overlay} ${styles.overlayVisible}`}
        onClick={handleClose}
        aria-hidden="true"
      />
      <aside
        className={`${styles.drawer} ${styles.drawerOpen}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={false}
        aria-label="Gamificación"
      >
        <button
          type="button"
          className={styles.closeBtn}
          onClick={handleClose}
          aria-label="Cerrar"
        >
          <IconX size={20} />
        </button>
        <div className={styles.body}>
          {widgetsEnabled && (
            <div
              ref={widgetsRef}
              className={styles.widgetsBlock}
              aria-busy={isHydrating}
            >
              <div className={styles.avatarSection}>
                <GamanzaWidgetSlot
                  dataType="avatar"
                  className={styles.avatarWidget}
                  isHydrating={isHydrating}
                  variant="circle"
                  size={140}
                  dataAttrs={{
                    "data-size": "140",
                    "data-show-level": "true",
                    "data-progress-type": "level",
                  }}
                />
                {isHydrating ? (
                  <span className={styles.usernameSkeleton} />
                ) : displayName ? (
                  <p className={styles.username}>{displayName}</p>
                ) : null}
              </div>

              <GamanzaWidgetSlot
                dataType="points"
                className={styles.pointsWidget}
                isHydrating={isHydrating}
                variant="block"
                minHeight={120}
                dataAttrs={{
                  "data-progress-type": "level",
                  "data-show-downgrade": "true",
                  "data-show-tooltip": "false",
                }}
              />

              <nav
                className={styles.linkList}
                aria-label="Secciones de gamificación"
              >
                {GAMANZA_DRAWER_LINKS.map(link => {
                  const Icon = LINK_ICONS[link.id] || IconShoppingBag;
                  return (
                    <button
                      key={link.id}
                      type="button"
                      className={styles.linkItem}
                      onClick={() => handleNavigate(link.path)}
                    >
                      <span className={styles.linkIcon}>
                        <Icon
                          size={20}
                          stroke={1.8}
                        />
                      </span>
                      <span className={styles.linkLabel}>{link.label}</span>
                      <IconChevronRight
                        className={styles.linkChevron}
                        size={18}
                        stroke={1.8}
                      />
                    </button>
                  );
                })}
              </nav>
            </div>
          )}
        </div>
      </aside>
    </>,
    document.body
  );
};

export default GamanzaPlayerDrawer;
